import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './utils';
import TenantInfoCard from './tenant/TenantInfoCard';
import NewTenancyWarningDialog from './tenant/NewTenancyWarningDialog';
import EditTenantDialog from './tenant/EditTenantDialog';
import TenantAIReviewDialog from './tenant/TenantAIReviewDialog';
import EmailRenewalDialog from './tenant/EmailRenewalDialog';

export default function TenantInfo({ property, propertyId, documents, onTenantUpdated }) {
  const location = useLocation();
  // Edit / New Tenancy form
  const [tenantEditDialogOpen, setTenantEditDialogOpen] = useState(false);
  const [newTenancyMode, setNewTenancyMode] = useState(false);
  const [tenantFormData, setTenantFormData] = useState({});

  // New Tenancy pre-confirm warning
  const [newTenancyWarningOpen, setNewTenancyWarningOpen] = useState(false);

  // AI extraction + review
  const [tenantAiLoading, setTenantAiLoading] = useState(false);
  const [tenantAiReview, setTenantAiReview] = useState(null);
  const [astFile, setAstFile] = useState(null); // UI hint only — file already uploaded

  // Email tenant renewal dialog
  const [emailRenewalOpen, setEmailRenewalOpen] = useState(false);

  // Deep-link: dashboard "Tenancies Ending Soon" rows pass
  // location.state.openNewTenancy=true to jump straight into the warning,
  // or location.state.openEmailRenewal=true to jump into the email dialog.
  useEffect(() => {
    if (location.state?.openNewTenancy) {
      setNewTenancyWarningOpen(true);
      window.history.replaceState({}, document.title);
    } else if (location.state?.openEmailRenewal) {
      setEmailRenewalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const openEditTenant = () => {
    setTenantFormData({
      tenant_name: property.tenant_name || '',
      tenant_email: property.tenant_email || '',
      tenant_phone: property.tenant_phone || '',
      rent_amount: property.rent_amount ?? '',
      rent_due_date: property.rent_due_date || 1,
      tenancy_start_date: property.tenancy_start_date || '',
      tenancy_end_date: property.tenancy_end_date || '',
      contract_type: property.contract_type || 'AST',
      deposit_amount: property.deposit_amount ?? '',
      contract_value: property.contract_value ?? '',
    });
    setTenantEditDialogOpen(true);
  };

  const openNewTenancy = () => {
    setNewTenancyMode(true);
    setNewTenancyWarningOpen(false);
    setAstFile(null);
    setTenantFormData({
      tenant_name: '',
      tenant_email: '',
      tenant_phone: '',
      rent_amount: '',
      rent_due_date: 1,
      tenancy_start_date: '',
      tenancy_end_date: '',
      contract_type: 'AST',
      deposit_amount: '',
      contract_value: '',
    });
    setTenantEditDialogOpen(true);
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...property,
        tenant_name: tenantFormData.tenant_name,
        tenant_email: (tenantFormData.tenant_email || '').trim() || null,
        tenant_phone: (tenantFormData.tenant_phone || '').trim() || null,
        rent_amount: parseFloat(tenantFormData.rent_amount),
        rent_due_date: parseInt(tenantFormData.rent_due_date, 10),
        tenancy_start_date: tenantFormData.tenancy_start_date,
        tenancy_end_date: tenantFormData.tenancy_end_date,
        contract_type: tenantFormData.contract_type,
        deposit_amount: tenantFormData.deposit_amount === '' || tenantFormData.deposit_amount == null
          ? null
          : parseFloat(tenantFormData.deposit_amount),
        contract_value: tenantFormData.contract_value === '' || tenantFormData.contract_value == null
          ? null
          : parseFloat(tenantFormData.contract_value),
      };

      // NEW TENANCY: archive current tenant snapshot before overwriting.
      if (newTenancyMode) {
        const snapshot = {
          tenant_name: property.tenant_name,
          rent_amount: property.rent_amount,
          rent_due_date: property.rent_due_date,
          tenancy_start_date: property.tenancy_start_date,
          tenancy_end_date: property.tenancy_end_date,
          contract_type: property.contract_type,
          deposit_amount: property.deposit_amount,
          contract_value: property.contract_value,
          archived_at: new Date().toISOString(),
        };
        payload.previous_tenancies = [...(property.previous_tenancies || []), snapshot];
      }

      await axios.put(`${API}/properties/${propertyId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(newTenancyMode ? 'New tenancy saved — previous tenant archived' : 'Tenant details updated');
      setTenantEditDialogOpen(false);
      setNewTenancyMode(false);
      setAstFile(null);
      await onTenantUpdated();
    } catch (error) {
      toast.error('Failed to update tenant details');
    }
  };

  // Upload the picked PDF DIRECTLY as the property's Tenancy Agreement
  // BEFORE running AI extraction. The document record exists in Mongo +
  // Cloudinary the moment the upload returns 200 — independent of what
  // happens with AI / dialog / save afterwards.
  const handleTenancyUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setTenantAiLoading(true);
    const token = localStorage.getItem('token');

    // Step 1: upload as Tenancy Agreement immediately.
    try {
      const existing = documents.find(d => d.document_type === 'tenancy_agreement');
      const fd = new FormData();
      fd.append('document_type', 'tenancy_agreement');
      const endDate = tenantFormData.tenancy_end_date || property.tenancy_end_date || '';
      if (endDate) fd.append('expiry_date', endDate);
      fd.append('file', file);
      await axios.post(`${API}/properties/${propertyId}/documents`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (existing?.id) {
        try {
          await axios.delete(`${API}/documents/${existing.id}`, { headers: { Authorization: `Bearer ${token}` } });
        } catch (_) { /* refresh reconciles */ }
      }
      setAstFile(file);
      toast.success('Tenancy Agreement attached to Documents');
      await onTenantUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload Tenancy Agreement');
      setTenantAiLoading(false);
      return;
    }

    // Step 2: AI extraction for auto-filling the form (independent of upload).
    try {
      const fd2 = new FormData();
      fd2.append('file', file);
      const response = await axios.post(`${API}/analyze-tenancy-upload`, fd2, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fields = response.data || {};
      const hasAny = Object.values(fields).some(v => v !== null && v !== undefined && v !== '');
      if (hasAny) {
        setTenantAiReview({ fields });
      } else {
        toast.info('AI couldn\u2019t auto-fill anything from that PDF — fill the fields in manually.');
      }
    } catch (error) {
      toast.warning('AI auto-fill unavailable — fill the fields in manually. The Tenancy Agreement is already attached.');
    } finally {
      setTenantAiLoading(false);
    }
  };

  const applyTenantAiReview = () => {
    const f = tenantAiReview?.fields || {};
    setTenantFormData(prev => ({
      ...prev,
      tenant_name: f.tenant_name ?? prev.tenant_name,
      tenant_email: f.tenant_email ?? prev.tenant_email,
      tenant_phone: f.tenant_phone ?? prev.tenant_phone,
      rent_amount: f.monthly_rent ?? prev.rent_amount,
      tenancy_start_date: f.tenancy_start_date ?? prev.tenancy_start_date,
      tenancy_end_date: f.tenancy_end_date ?? prev.tenancy_end_date,
      contract_type: f.contract_type ?? prev.contract_type,
      deposit_amount: f.deposit_amount ?? prev.deposit_amount,
    }));
    setTenantAiReview(null);
    toast.success('Tenant details applied — review and save');
  };

  return (
    <>
      <TenantInfoCard
        property={property}
        onEditTenant={openEditTenant}
        onNewTenancy={() => setNewTenancyWarningOpen(true)}
        onEmailRenewal={() => setEmailRenewalOpen(true)}
      />

      <NewTenancyWarningDialog
        open={newTenancyWarningOpen}
        onOpenChange={setNewTenancyWarningOpen}
        onCancel={() => setNewTenancyWarningOpen(false)}
        onContinue={openNewTenancy}
      />

      <EditTenantDialog
        open={tenantEditDialogOpen}
        onOpenChange={(open) => {
          setTenantEditDialogOpen(open);
          if (!open) { setNewTenancyMode(false); setAstFile(null); }
        }}
        newTenancyMode={newTenancyMode}
        formData={tenantFormData}
        setFormData={setTenantFormData}
        astFile={astFile}
        aiLoading={tenantAiLoading}
        onTenancyUpload={handleTenancyUpload}
        onSubmit={handleEditTenant}
      />

      <TenantAIReviewDialog
        review={tenantAiReview}
        setReview={setTenantAiReview}
        onApply={applyTenantAiReview}
      />

      <EmailRenewalDialog
        open={emailRenewalOpen}
        onOpenChange={setEmailRenewalOpen}
        property={{ id: propertyId, ...property }}
      />
    </>
  );
}
